import React, { useState, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Money01Icon,
  Tick01Icon,
  Cancel01Icon,
  BankIcon,
  UserCircleIcon,
  SmartPhone01Icon,
  InformationCircleIcon,
  Clock01Icon,
  Layers01Icon,
  ArrowRight01Icon,
  Copy01Icon
} from '@hugeicons/core-free-icons';
import { api } from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import { FadeLoader } from 'react-spinners';
import Button from '../../components/Button';
import ResponsiveModal from '../../components/ResponsiveModal';
import ReelText from '../../components/ui/ReelText';

const AgentCommissions = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [filter, setStatusFilter] = useState('pending');
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [processing, setProcessing] = useState(false);
  const { addToast } = useToast();

  const fetchCommissions = async (page = 1) => {
    try {
      const res = await api.get(`/agent/commissions?status=${filter}&page=${page}`);
      setRequests(res.data.data || []);
      setPagination({
        current_page: res.data.current_page || 1,
        last_page: res.data.last_page || 1,
        total: res.data.total || 0
      });
    } catch (err) {
      addToast("Failed to load payout requests", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommissions(1);
  }, [filter]);

  const handleViewAccount = (payout) => {
    setSelectedPayout(payout);
    setIsAccountModalOpen(true);
  };

  const handleApprove = async (payout) => {
    if (!window.confirm("Confirm you have sent the bank transfer. This action is final.")) return;
    setProcessing(true);
    try {
      await api.post(`/agent/commissions/${payout.uid}/mark-paid`);
      addToast("Payout marked as completed", "success");
      fetchCommissions();
    } catch (err) {
      addToast(err.message || "Failed to update payout", "error");
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectClick = (payout) => {
    setSelectedPayout(payout);
    setIsAccountModalOpen(false);
    setIsRejectModalOpen(true);
  };

  const processReject = async () => {
    setProcessing(true);
    try {
      await api.post(`/agent/commissions/${selectedPayout.uid}/reject`, { note: rejectNote || 'Rejected by agent' });
      addToast("Payout request rejected", "info");
      setIsRejectModalOpen(false);
      setRejectNote('');
      fetchCommissions();
    } catch (err) {
      addToast(err.message || "Failed to reject payout", "error");
    } finally {
      setProcessing(false);
    }
  };

  if (loading && requests.length === 0) {
    return <div className="min-h-[60vh] flex items-center justify-center"><FadeLoader color="#4ade80" /></div>;
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-24 sm:pb-32">
      <div className="bg-white/10 backdrop-blur-xl p-8 rounded-[2.5rem] border border-white/20">
        <h1 className="text-3xl font-black text-charcoal dark:text-white flex items-center gap-3">
          <div className="p-3 bg-soft-green/20 rounded-2xl text-soft-green"><HugeiconsIcon icon={Money01Icon} size={28} /></div>
          Payout Management
        </h1>
        <p className="text-muted-gray mt-2 font-medium">Settle cashback earnings requested by your clients.</p>
      </div>

      <div className="flex gap-2 p-1.5 bg-white/50 dark:bg-black/20 w-full sm:w-fit rounded-2xl border border-white/10 justify-around sm:justify-start">
        {[
          { id: 'pending', icon: Clock01Icon },
          { id: 'completed', icon: Tick01Icon },
          { id: 'rejected', icon: Cancel01Icon },
          { id: 'all', icon: Layers01Icon }
        ].map(s => (
          <button 
            key={s.id} 
            onClick={() => setStatusFilter(s.id)}
            className={`flex items-center gap-2 px-4 sm:px-6 py-2 rounded-xl transition-all ${filter === s.id ? 'bg-soft-green text-white shadow-lg scale-105' : 'text-muted-gray hover:text-charcoal dark:hover:text-white'}`}
          >
            <HugeiconsIcon icon={s.icon} size={18} className="sm:hidden" />
            <span className={`text-xs font-black uppercase tracking-widest whitespace-nowrap ${filter === s.id ? 'block' : 'hidden sm:block'}`}>{s.id}</span>
          </button>
        ))}
      </div>

      {filter === 'pending' ? (
        <div className="grid grid-cols-1 gap-4">
          {requests.length === 0 ? (
            <div className="p-20 text-center bg-white/5 rounded-[3rem] border border-dashed border-white/10">
              <HugeiconsIcon icon={InformationCircleIcon} size={48} className="text-muted-gray mx-auto mb-4 opacity-20" />
              <p className="text-muted-gray font-bold uppercase tracking-[0.2em] opacity-40 text-xs">No pending requests found.</p>
            </div>
          ) : (
            requests.map(req => (
              <div key={req.uid} className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 p-5 sm:p-8 flex items-center justify-between gap-4 shadow-sm hover:shadow-float transition-all group">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-12 h-12 bg-purple-500/10 rounded-2xl flex items-center justify-center text-purple-500 shrink-0">
                    <HugeiconsIcon icon={UserCircleIcon} size={24} />
                  </div>
                  <div className="min-w-0 text-left">
                    <p className="text-[8px] font-black text-muted-gray uppercase tracking-widest mb-0.5">Requested By</p>
                    <p className="text-sm sm:text-xl font-black text-charcoal dark:text-white truncate">
                      {req.client?.business_name || req.client?.email.split('@')[0] || 'Client'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 sm:gap-8">
                  <div className="text-right">
                    <p className="text-[8px] font-black text-muted-gray uppercase tracking-widest mb-0.5">Payout Amount</p>
                    <p className="text-lg sm:text-2xl font-black text-soft-green">
                      ₦{parseFloat(req.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <button 
                    onClick={() => handleViewAccount(req)}
                    className="p-3 bg-soft-green text-white rounded-xl shadow-lg hover:scale-105 active:scale-95 transition-all"
                  >
                    <HugeiconsIcon icon={ArrowRight01Icon} size={20} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="bg-white/50 dark:bg-black/20 rounded-[2.5rem] border border-white/10 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-black/5 dark:bg-white/5 text-muted-gray text-[10px] uppercase font-black tracking-widest">
                <tr>
                  <th className="px-8 py-5">Client</th>
                  <th className="px-8 py-5">Amount</th>
                  <th className="px-8 py-5">Status</th>
                  <th className="px-8 py-5 text-right">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {requests.map(req => (
                  <tr key={req.uid} className="text-sm font-bold text-charcoal dark:text-white hover:bg-white/5 transition-colors">
                    <td className="px-8 py-6 min-w-[200px]">
                      <p className="font-black leading-tight truncate">{req.client?.business_name || req.client?.email?.split('@')[0]}</p>
                      <p className="text-[10px] text-muted-gray font-medium mt-0.5">{req.client?.email}</p>
                    </td>
                    <td className="px-8 py-6 font-black text-soft-green">₦{parseFloat(req.amount).toLocaleString()}</td>
                    <td className="px-8 py-6 uppercase font-black text-[10px]">
                      <span className={req.status === 'completed' ? 'text-soft-green' : req.status === 'rejected' ? 'text-red-500' : 'text-yellow-500'}>
                        {req.status}
                      </span>
                    </td>
                    <td className="px-8 py-6 text-right text-muted-gray text-xs font-medium">{new Date(req.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pagination.last_page > 1 && (
        <div className="flex justify-between items-center bg-white/50 dark:bg-black/20 p-6 rounded-[2rem] border border-white/10">
          <p className="text-[10px] font-black uppercase text-muted-gray">Total: {pagination.total} Settlements</p>
          <div className="flex gap-2">
            <button 
              disabled={pagination.current_page === 1}
              onClick={() => fetchCommissions(pagination.current_page - 1)}
              className="px-4 py-2 bg-white dark:bg-white/5 rounded-xl text-[10px] font-black uppercase disabled:opacity-30 border border-gray-100 dark:border-white/5"
            >
              Prev
            </button>
            <span className="px-4 py-2 text-[10px] font-black text-charcoal dark:text-white">{pagination.current_page} / {pagination.last_page}</span>
            <button 
              disabled={pagination.current_page === pagination.last_page}
              onClick={() => fetchCommissions(pagination.current_page + 1)}
              className="px-4 py-2 bg-white dark:bg-white/5 rounded-xl text-[10px] font-black uppercase disabled:opacity-30 border border-gray-100 dark:border-white/5"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <ResponsiveModal isOpen={isAccountModalOpen} onClose={() => setIsAccountModalOpen(false)} title="Payout Account Details">
        <div className="py-2">
          <div className="bg-gradient-to-br from-soft-green/20 to-soft-green/5 p-8 rounded-[2.5rem] border border-soft-green/20 text-center mb-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-5 rotate-12">
              <HugeiconsIcon icon={BankIcon} size={120} />
            </div>
            
            <div className="w-20 h-20 bg-white dark:bg-dark-surface rounded-[2rem] flex items-center justify-center text-soft-green mx-auto mb-4 shadow-float-sm relative z-10 border-4 border-white dark:border-dark-surface">
              <HugeiconsIcon icon={BankIcon} size={40} />
            </div>
            
            <div className="relative z-10">
              <p className="text-[10px] font-black uppercase text-muted-gray tracking-widest mb-1">Destination Bank</p>
              <h3 className="text-2xl font-black text-charcoal dark:text-white">{selectedPayout?.bank_details?.bank_name}</h3>
            </div>
          </div>

          <div className="space-y-3 px-2">
            <div className="p-5 bg-off-white dark:bg-gray-800/50 rounded-3xl border border-light-gray/20 dark:border-gray-700/50 flex items-center gap-4 group hover:bg-white dark:hover:bg-gray-800 transition-all">
              <div className="p-3 bg-white dark:bg-dark-surface rounded-2xl text-muted-gray shadow-sm group-hover:text-soft-green transition-colors">
                <HugeiconsIcon icon={UserCircleIcon} size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[9px] font-black uppercase text-muted-gray tracking-widest mb-0.5">Account Name</p>
                <p className="font-bold text-charcoal dark:text-white truncate text-sm">{selectedPayout?.bank_details?.account_name}</p>
              </div>
            </div>

            <div className="p-5 bg-off-white dark:bg-gray-800/50 rounded-3xl border border-light-gray/20 dark:border-gray-700/50 flex items-center gap-4 group hover:bg-white dark:hover:bg-gray-800 transition-all">
              <div className="p-3 bg-white dark:bg-dark-surface rounded-2xl text-muted-gray shadow-sm group-hover:text-soft-green transition-colors">
                <HugeiconsIcon icon={Money01Icon} size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[9px] font-black uppercase text-muted-gray tracking-widest mb-0.5">Account Number</p>
                <p className="font-mono font-black text-charcoal dark:text-white text-lg">{selectedPayout?.bank_details?.account_number}</p>
              </div>
              <button 
                onClick={() => { navigator.clipboard.writeText(selectedPayout?.bank_details?.account_number); addToast('Copied!', 'success'); }}
                className="p-2 text-soft-green hover:scale-110 transition-transform"
              >
                <HugeiconsIcon icon={Copy01Icon} size={20} />
              </button>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-3 px-2">
            <Button onClick={() => { handleApprove(selectedPayout); setIsAccountModalOpen(false); }} disabled={processing} className="w-full py-4 rounded-2xl font-black shadow-lg">
              {processing ? 'Processing...' : `Confirm ₦${parseFloat(selectedPayout?.amount || 0).toLocaleString()} Paid`}
            </Button>
            <Button onClick={() => handleRejectClick(selectedPayout)} variant="secondary" disabled={processing} className="w-full py-4 rounded-2xl font-black text-red-500 bg-red-50 dark:bg-red-900/10 border-none">
              Reject Request
            </Button>
          </div>
        </div>
      </ResponsiveModal>

      <ResponsiveModal isOpen={isRejectModalOpen} onClose={() => setIsRejectModalOpen(false)} title="Reject Payout">
        <div className="py-4 space-y-6">
          <div className="bg-red-500/10 p-6 rounded-[2rem] text-center border border-red-500/20">
            <p className="text-sm text-red-600 dark:text-red-400 font-bold">You are about to reject the payout for ₦{parseFloat(selectedPayout?.amount || 0).toLocaleString()}.</p>
          </div>
          
          <div className="space-y-2">
            <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest px-1">Reason for Rejection (Optional)</label>
            <textarea 
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="e.g. Invalid bank details, account name mismatch..."
              className="w-full h-32 px-6 py-5 bg-off-white dark:bg-gray-800 border-none rounded-[2rem] outline-none font-bold text-sm text-charcoal dark:text-white focus:ring-2 focus:ring-red-500/50"
            />
          </div>

          <div className="flex flex-col gap-3">
            <Button onClick={processReject} disabled={processing} className="w-full py-4 rounded-2xl font-black bg-red-500 text-white border-none shadow-lg shadow-red-500/20">
              Confirm Rejection
            </Button>
            <Button variant="secondary" onClick={() => { setIsRejectModalOpen(false); setIsAccountModalOpen(true); }} className="w-full py-4 rounded-2xl font-black">
              Cancel
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default AgentCommissions;
